import React, {useState} from 'react';
import {
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import {useDispatch, useSelector} from 'react-redux';
import BTButton from '../Components/BTButton';
import BTLinearGradient from '../Components/BTLinearGradient';
import {ShowToast, retrieveSetting, storeSetting} from '../Common/common';
import {
  useSignOutMutation,
  useUpdateUserPasswdMutation,
} from '../redux/services/ausersApi';
import {resetState, updateState} from '../redux/services/authSlice';
import {RootState} from '../redux/store';
import {borderRadius, colors, spacing} from '../theme/theme';

import T from '../Components/T';
import { tp } from '../utils/translateText';
// Shown instead of the app while an administrator has flagged the account
// "change password at next login" (gdata.mustChangePwd). The API also rejects
// every other request until the password is changed.
const ForcePasswordChange = () => {
  const dispatch = useDispatch();
  const {user} = useSelector((state: RootState) => state.gdata);
  const [updatePwd, {isLoading}] = useUpdateUserPasswdMutation();
  const [signOut] = useSignOutMutation();
  const [oldPwd, setOldPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');

  const onSubmit = async () => {
    if (!oldPwd) {
      ShowToast('Current password is required', 'warning');
    } else if (newPwd.length < 6 || newPwd.length > 16) {
      ShowToast('New password must be 6 to 16 characters', 'warning');
    } else if (newPwd === oldPwd) {
      ShowToast('New password must differ from the current password', 'warning');
    } else if (newPwd !== confirmPwd) {
      ShowToast('Passwords must match', 'warning');
    } else {
      try {
        const res: any = await updatePwd({
          cp_user: user,
          cp_oldpwd: oldPwd,
          cp_newpwd: newPwd,
          cp_newcpwd: confirmPwd,
        }).unwrap();
        // Keep the "remember me" password in step so auto-login doesn't use the old one
        if (await retrieveSetting('pwd')) {
          await storeSetting('pwd', newPwd);
        }
        ShowToast(res?.msg || 'Password changed successfully', 'success');
        dispatch(updateState({mustChangePwd: false}));
      } catch (err: any) {
        ShowToast(
          (typeof err?.data === 'string' ? err.data : err?.data?.message) ||
            'Failed to change password',
          'error',
        );
      }
    }
  };

  const onCancel = async () => {
    try {
      await signOut({}).unwrap();
    } catch (e) {
      // the session is dropped locally either way
    }
    dispatch(resetState());
  };

  return (
    <BTLinearGradient style={styles.gradient}>
      <KeyboardAvoidingView
        style={styles.gradient}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <ScrollView contentContainerStyle={styles.scroll}>
          <View style={styles.card}>
            <Text style={styles.title}><T>Change Password</T></Text>
            <Text style={styles.subtitle}>
              <T>Your administrator requires you to change your password before
              continuing.</T>
            </Text>
            <TextInput
              style={styles.input}
              placeholder={tp("Current Password")}
              secureTextEntry
              autoCapitalize="none"
              value={oldPwd}
              onChangeText={setOldPwd}
            />
            <TextInput
              style={styles.input}
              placeholder={tp("New Password")}
              secureTextEntry
              autoCapitalize="none"
              value={newPwd}
              onChangeText={setNewPwd}
            />
            <TextInput
              style={styles.input}
              placeholder={tp("Confirm New Password")}
              secureTextEntry
              autoCapitalize="none"
              value={confirmPwd}
              onChangeText={setConfirmPwd}
              onSubmitEditing={onSubmit}
            />
            <BTButton
              title="Change Password"
              onPress={onSubmit}
              onLongPress={undefined}
              color={undefined}
              loading={isLoading}
            />
            <TouchableOpacity onPress={onCancel} style={styles.cancel}>
              <Text style={styles.cancelText}><T>Cancel and sign out</T></Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </BTLinearGradient>
  );
};

const styles = StyleSheet.create({
  gradient: {flex: 1, width: '100%'},
  scroll: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.lg,
  },
  card: {
    width: '100%',
    maxWidth: 450,
    backgroundColor: 'rgba(255, 255, 255, 0.98)',
    borderRadius: borderRadius['3xl'],
    padding: spacing['2xl'],
  },
  title: {fontSize: 22, fontWeight: '700', color: colors.black},
  subtitle: {fontSize: 14, color: '#555', marginVertical: spacing.base},
  input: {
    borderWidth: 1,
    borderColor: '#ccc',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginBottom: spacing.base,
    color: colors.black,
  },
  cancel: {alignItems: 'center', marginTop: spacing.base},
  cancelText: {color: '#555', textDecorationLine: 'underline'},
});

export default ForcePasswordChange;
